/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import useEvent from 'react-use-event-hook';
import { DateRangeType } from 'react-tailwindcss-datepicker';
import dayjs from 'dayjs';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
} from 'components/layouts/SearchBox';
import CustomDatepicker from 'components/inputs/CustomDatepicker';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { AsstTpCd, IdleAssetCondition, openEmsSPLink, RvwStatCd } from 'models/asst/IdleAsset';
import UserModal from 'components/modals/common/UserModal';
import { Employee } from 'models/admin/Employee';
import _ from 'lodash';
import { CellType } from '@grapecity/wijmo.grid';
import {
  ControlBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
  GlobalBtnGroup,
} from '../../components/layouts/ContentLayout';
import { Button } from '@mui/material';
import { IconButton } from '../../components/buttons/IconSVG';
import { findIdleAssetMaster, generateRvwReqId } from '../../apis/asst/IdleAsset';
import { ApproveRequestPageType } from '../approves/ApproveRequestDetailCase';
import { useMessageBar } from '../../hooks/useMessageBar';
import CustomGrid from '../../components/grids/CustomGrid';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { IdleAssetMaster } from 'models/asst/IdleAsset';
import { getExcelFileName } from 'utils/ExcelUtil';
import ApproveRequestModal from '../approves/ApproveRequestModal';
import { ManagementMode } from '../../models/common/Common';
import GridSettingButtons from '../../components/grids/GridSettingButtons';
import { useSearchFilterStore } from './useSearchFilterStore';
import moment from 'moment';

interface DisplayCondition {
  dataInsUserNm?: string; // 요청자명
}

interface PageLocationState {
  copCd?: string;
  mode?: ManagementMode;
  searchParam?: IdleAssetCondition;
}

/**
 * 불용자산 검토요청 목록
 * @constructor
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.22  lekpro          최초생성
 * 2026.01.14  jihwan89        요청날짜 default값 수정 1년전 1월1일부터 현재까지
 */
const IdlAssetReviewListPage = () => {
  const { t } = useTranslation();

  const today = moment().format('YYYY-MM-DD');
  const bfOneYear = moment().subtract(1, 'years');
  const startYears = bfOneYear.startOf('month').format('YYYY-MM-DD');

  const defaultCondition = {
    copCds: [] as string[],
    copCd: '',
    startDate: startYears,
    endDate: dayjs().format('YYYY.MM.DD'),
    asstTpCd: '',
  };

  const gridRef = useRef<any>();
  const gridRef2 = useRef<any>();

  const [rowData, setRowData] = useState<IdleAssetMaster[]>([]);
  const [isOpenRequestModal, setOpenRequestModal] = useState<boolean>(false);

  const [condition, setCondition] = useState<IdleAssetCondition>({ ...defaultCondition });
  const [displayCondition, setDisplayCondition] = useState<DisplayCondition>({});
  const [code, setCode] = useState<any>();
  const [isOpenUserModal, setOpenUserModal] = useState<boolean>(false);

  const [requestModalCondition, setRequestModalCondition] = useState<any>();
  const { openMessageBar } = useMessageBar();
  const [isChangeColumn, setIsChangeColumn] = useState<boolean>(false);
  const navigate = useNavigate();
  const locationState: PageLocationState = useLocation().state;

  const isNotNullCheck = (val) => {
    return typeof val !== 'undefined' && !_.isEmpty(val);
  };

  const { searchFilters, setSearchFilters } = useSearchFilterStore();
  const [forceSearch, setForceSearch] = useState<boolean>(false);
  useEffect(() => {
    if (isNotNullCheck(locationState?.searchParam?.rvwReqId)) return;

    if (isNotNullCheck(locationState?.searchParam)) {
      const initialCondition = {
        ...condition,
        startDate: locationState?.searchParam?.startDate,
        endDate: locationState?.searchParam?.endDate,
        rvwStatCd: locationState?.searchParam?.rvwStatCd,
      };

      const copCd = locationState?.searchParam?.copCd;
      const tmp: string[] = [];
      if (!_.isEmpty(copCd)) {
        tmp.push(copCd as string);
        // initialCondition['copCds'] = tmp;
        // initialCondition['copCd'] = copCd;
        initialCondition['plblCop'] = copCd;
        initialCondition['plblYn'] = 'Y';
      }
      const rvwRltDivsCd = locationState?.searchParam?.rvwRltDivsCd;
      if (isNotNullCheck(rvwRltDivsCd)) initialCondition['rvwRltDivsCd'] = rvwRltDivsCd;
      setCondition(initialCondition);
      setForceSearch(!forceSearch);
      //setTimeout(() => handleSearch, 3000);
    } else {
      try {
        // setCondition(searchFilters);
        gridRef.current.select(searchFilters.currentRow, searchFilters.currentCol);
      } catch (e) {
        // console.log('err', e);
      }
    }
  }, [locationState?.searchParam]);

  useEffect(() => {
    // setCondition(defaultCondition);
    getCommonCodes();
    setForceSearch(!forceSearch);
  }, []);

  useEffect(() => {
    handleSearch();
  }, [forceSearch]);

  const handleExportExcel = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(gridRef.current, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = t('asst.label.불용자산 검토목록 List', '불용자산 검토목록 List');
    book.saveAsync(
      getExcelFileName(t('asst.label.불용자산 검토목록 List', '불용자산 검토목록 List'))
    );
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
    }
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    gridRef.current.formatItem.addHandler((s, e) => {
      const col = s.columns[e.col];
      const binding = col.binding;
      try {
        e.cell.style.textDecoration = 'none';
        e.cell.style.color = '#000000';
        e.cell.style.cursor = 'default';
        e.cell.style.textUnderlinePosition = 'none';

        if (
          CellType.Cell === e.panel.cellType &&
          (binding == 'rvwReqId' || binding == 'rvwReqTitNm' || binding == 'progressRate')
        ) {
          e.cell.style.textDecoration = 'underline';
          e.cell.style.color = '#2D9BB2';
          e.cell.style.cursor = 'pointer';
          e.cell.style.textUnderlinePosition = 'under';
        }
      } catch (e) {
        console.log(e);
      }
    });

    grid.hostElement.addEventListener('click', (e: MouseEvent) => {
      const len = grid.rows.length;
      if (len == 0) return;
      const ht = grid.hitTest(e);

      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return; // 선택된 영역이 셀이 아니면 나가리.

      if (ht.panel === grid.cells) {
        const item = grid.rows[ht.row].dataItem;
        const binding = grid.columns[ht.col].binding;

        if (['progressRate', 'rvwReqTitNm', 'rvwReqId'].indexOf(binding) > -1) {
          const z = condition;
          z['currentRow'] = ht.row;
          z['currentCol'] = ht.col;
          setSearchFilters(z);
          goNavigation(item, binding);
        }
      }
    });

    setIsChangeColumn(true);
    gridRef.current.refresh();
  };

  const goNavigation = (item, strColBinding): void => {
    const irvwReqId = item.rvwReqId;

    let mappingURL = '/asset/review-result-detail';
    let mappingNm = t('asst.label.불용자산 검토목록', '불용자산 검토목록');

    if (strColBinding == 'rvwReqId') {
      if (AsstTpCd.SPARE_PARTS == item.asstTpCd) {
        // build12 / !dlatl789
        openEmsSPLink(item.aprReqId);
      } else {
        setRequestModalCondition({
          pageId: ApproveRequestPageType.IDLE_ASST_REQ,
          aprReqId: item.aprReqId,
          rvwReqId: irvwReqId,
          rvmAsstTpCd: item?.asstTpCd,
        });
        setOpenRequestModal(true);
      }
      return;
    }

    /*
      불용자산 검토요청 회신 화면 이동(담당자 검토하는 화면) 설비/기타자산은 동일 화면,  SP는 별도 화면검토완료, 검토 담당자가 아닌 경우는 검토 내역
     'CPLT'
     */
    if (strColBinding == 'rvwReqTitNm') {
      if ('N' == item.deptRvwRoleYn || RvwStatCd.COMPLETE == item.rvwStatCd) {
        mappingURL = '/asset/review-result-detail';
        mappingNm = t('asst.label.불용자산 검토내역', '불용자산 검토내역');
      } else {
        mappingURL = '/asset/review-request-reply';
        mappingNm = t('asst.label.불용자산 검토회신', '불용자산 검토회신');
      }
    }

    if (strColBinding == 'progressRate') {
      mappingURL = '/asset/review-result-detail';
      mappingNm = t('asst.label.불용자산 검토내역', '불용자산 검토내역');
    }
    navigate(mappingURL, {
      state: {
        rvwReqId: irvwReqId,
        asstTpCd: item.asstTpCd,
        upprMnuUrl: location.pathname,
        mode: item.rvwStatCd === RvwStatCd.COMPLETE ? ManagementMode.READ : ManagementMode.MODIFY,
        mnuId: `${location.pathname}`,
        mnuNm: mappingNm,
        searchParam: {
          rvwReqId: irvwReqId,
          asstTpCd: item.asstTpCd,
        },
      },
    });
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        align: 'center',
        width: 40,
        isReadOnly: true,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'asstTpCdNm',
        header: String(t('asst.label.분류', '분류')),
        align: 'center',
        width: 80,
        isReadOnly: true,
      },
      {
        binding: 'asstRvwReqCopCd',
        header: String(t('asst.label.요청법인', '요청법인')),
        align: 'center',
        width: 90,
      },
      {
        binding: 'rvwReqId',
        header: String(t('asst.label.요청번호', '요청번호')),
        align: 'center',
        width: 90,
      },
      {
        binding: 'rvwReqTitNm',
        header: String(t('asst.label.요청명', '요청명')),
        align: 'left',
        width: '*',
        minWidth: 150,
      },
      {
        binding: 'rvwReqCtn',
        header: String(t('asst.label.요청내용', '요청내용')),
        align: 'left',
        width: 250,
        cellTemplate: (params) => params.item.rvwReqCtn || '',
      },

      {
        binding: 'eqpCount',
        header: String(t('asst.label.수량', '수량')),
        align: 'center',
        width: 65,
      },
      {
        binding: 'rvwStatCdNm',
        header: String(t('asst.label.검토상태', '검토상태')),
        align: 'center',
        width: 100,
      },
      {
        binding: 'progressRate',
        header: String(t('asst.label.검토율', '검토율')),
        align: 'center',
        width: 100,
      },
      {
        binding: 'rvwCpltDt',
        header: String(t('asst.label.검토기한', '검토기한')),
        align: 'center',
        width: 90,
      },
      {
        binding: 'rvwRltDivsCd',
        header: String(t('asst.label.검토결과', '검토결과')),
        align: 'center',
        width: 88,
        cssClass: 'WijmoTag',
        cellTemplate: (item) => {
          if ('PSBL' === item.value) {
            return `<span class="green">${t('asst.label.활용가능', '활용가능')}</span>`;
          } else if ('BOOK' === item.value) {
            return `<span class="yellow" style="width:56px">${t(
              'asst.label.예약중',
              '예약중'
            )}</span>`;
          } else if (!_.isEmpty(item.value)) {
            return `<span class="red">${t('asst.label.활용불가', '활용불가')}</span>`;
          } else {
            return `<span></span>`;
          }
        },
      },
      {
        binding: 'tgtCopCd',
        header: String(t('asst.label.활용법인', '활용법인')),
        align: 'left',
        width: 120,
        isReadOnly: true,
      },
      {
        binding: 'rvwDt',
        header: String(t('asst.label.최근검토일자', '최근검토일자')),
        align: 'center',
        width: 105,
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('asst.label.요청자', '요청자')),
        align: 'center',
        width: 90,
        isReadOnly: true,
      },
    ];
  }, [isChangeColumn]);

  const getCommonCodes = async () => {
    const elmCopCd: Code[] = await getCommonCodeNames('ELM_COP_CD'); // 법인코드
    const asstRvwRltDivsCd: Code[] = await getCommonCodeNames('RVW_RLT_DIVS_CD'); // 검토결과
    const orgAsstTpCd: Code[] = await getCommonCodeNames('ASST_TP_CD'); // 검토결과
    const asstTpCd: Code[] = orgAsstTpCd.filter((obj) => !(obj.cmnCd === AsstTpCd.SPARE_PARTS));
    const rvwStatCd: Code[] = await getCommonCodeNamesCondition({
      optValCtn1: 'Y',
      cmnGrCd: 'RVW_STAT_CD',
    }); // 검토상태
    asstRvwRltDivsCd.unshift({ cmnCd: 'BOOK', cmnCdNm: '예약중' });

    setCode({
      elmCopCd: elmCopCd,
      asstRvwRltDivsCd: asstRvwRltDivsCd,
      rvwStatCd: rvwStatCd,
      asstTpCd: asstTpCd,
    });
  };

  const handleSearch = () => {
    findIdleAssetMaster(condition).then((result) => {
      if (result == null) return;

      //  setSearchFilters(condition);

      const cleanData = result.map((obj) =>
        Object.fromEntries(
          Object.entries(obj).map(([key, value]) => [key, value === null ? '' : value])
        )
      );
      // console.log('search', condition?.rvwRltDivsCd);
      if (condition?.rvwRltDivsCd === 'BOOK') {
        const booksData = cleanData.filter((item) => item.rvwRltDivsCd === 'BOOK');
        setRowData(booksData);
      } else {
        setRowData(cleanData);
      }
    });
  };

  const handleConditionChange = (name, value) => {
    setCondition({ ...condition, [name]: value });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols className="width25">
                <label>
                  <span>{String(t('asst.label.법인', '법인'))}</span>
                </label>
                <MultiComboBox
                  id="copCd"
                  placeholder={String(t('asst.msg.요청법인', '요청법인'))}
                  options={code?.elmCopCd || []}
                  defaultValue={condition?.copCds || []}
                  onChange={(value) => {
                    try {
                      handleConditionChange('copCd', (value || []).join());
                      // console.log('MultiComboBox', value);
                      //  console.log('XXXXX', value.join(', ')); // 헤더 필터용
                    } catch (e) {
                      //console.log('MultiComboBox2', e);
                    }
                  }}
                />
              </SearchCols>
              <SearchCols className="width25">
                <label>
                  <span>{String(t('asst.label.요청명', '요청명'))}</span>
                </label>
                <CustomInputWithSearch
                  name="rvwReqTitNm"
                  placeholder={String(
                    t('asst.msg.요청명을 입력해 주세요.', '요청명을 입력해 주세요.')
                  )}
                  value={condition?.rvwReqTitNm}
                  onChange={(e) => {
                    handleConditionChange(e.target.name, e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols className="width25">
                <label>
                  <span>{String(t('asst.label.설비ID', '설비ID'))}</span>
                </label>
                <CustomInputWithSearch
                  name="eqpId"
                  placeholder={String(
                    t('asst.msg.설비ID을 입력해 주세요.', '설비ID을 입력해 주세요.')
                  )}
                  value={condition?.eqpId}
                  onChange={(e) => {
                    handleConditionChange(e.target.name, e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>

              <SearchCols className="width25">
                <label>
                  <span>{String(t('asst.label.설비명', '설비명'))}</span>
                </label>
                <CustomInputWithSearch
                  name="eqpNm"
                  placeholder={String(
                    t('asst.msg.설비명을 입력해 주세요.', '설비명을 입력해 주세요.')
                  )}
                  value={condition?.eqpNm}
                  onChange={(e) => {
                    handleConditionChange(e.target.name, e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols className="width25">
                <label>
                  <span>{String(t('asst.label.자산명', '자산명'))}</span>
                </label>
                <CustomInputWithSearch
                  name="asstNm"
                  placeholder={String(
                    t('asst.msg.자산명을 입력해주세요.', '자산명을 입력해주세요.')
                  )}
                  value={condition?.asstNm}
                  onChange={(e) => {
                    handleConditionChange(e.target.name, e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols className="width25">
                <label>
                  <span>{String(t('asst.label.검토상태', '검토상태'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('asst.msg.선택', '선택'))}
                  options={code?.rvwStatCd || []}
                  defaultValue={condition?.rvwStatCd}
                  onChange={(value) => handleConditionChange('rvwStatCd', value)}
                />
              </SearchCols>
              <SearchCols className="width25">
                <label>
                  <span>{String(t('asst.label.검토결과', '검토결과'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('asst.msg.선택', '선택'))}
                  options={code?.asstRvwRltDivsCd || []}
                  defaultValue={condition?.rvwRltDivsCd}
                  onChange={(value) => handleConditionChange('rvwRltDivsCd', value)}
                />
              </SearchCols>
              {/*<SearchCols className="width25">
                <label>
                  <span>{String(t('asst.label.활용법인', '활용법인'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('asst.msg.선택', '선택'))}
                  options={code?.elmCopCd || []}
                  defaultValue={condition?.plblCop}
                  onChange={(value) => handleConditionChange('plblCop', value)}
                />
              </SearchCols>*/}
              <SearchCols className="width25">
                <label>
                  <span>{String(t('asst.label.요청일자', '요청일자'))}</span>
                </label>
                <CustomDatepicker
                  isRange={true} // 기간선택인 경우 생략가능
                  value={{
                    startDate: condition?.startDate || '',
                    endDate: condition?.endDate || '',
                  }}
                  onChange={(newValue) => {
                    const value = newValue as DateRangeType;
                    setCondition({
                      ...condition,
                      startDate: (value?.startDate as string) || '',
                      endDate: (value?.endDate as string) || '',
                    });
                  }}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            defaultCondition={defaultCondition}
            setReset={setCondition}
            onSearch={() => handleSearch()}
          />
        </SearchBoxRow>
      </SearchBox>

      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('asst.label.불용자산 검토목록 List', '불용자산 검토목록 List')}</h3>
          <span className="total">
            {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {/*<ComboBox
            placeholder={String(t('asst.msg.활용법인', '활용법인'))}
            options={code?.elmCopCd || []}
            defaultValue={condition?.plblCop}
            onChange={(value) => handleConditionChange('plblCop', value)}
            style={{ width: '100px' }}
          />*/}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleExportExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <GridSettingButtons
            gridId={'idleAssetReviewListGrid'} // <CustomGrid>에 선언한 id와 동일한 값
            layoutDefinition={layoutDefinition} // 그리드 레이아웃
            changeShownColumns={(result) => {
              // 노출필드 변경이벤트
              // ref를 이용해 <CustomGrid> 내 함수를 실행
              gridRef2.current?.changeColumn?.(result);
              setIsChangeColumn(true);
            }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={'idleAssetReviewListGrid'}
        ref={gridRef2}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={550}
        isReadOnly={true}
        isSelector={false}
        allowPinning={false}
        initialized={onInitialized}
      />

      <GlobalBtnGroup>
        <ComboBox
          placeholder={String(t('asst.msg.선택', '선택'))}
          options={code?.asstTpCd || []}
          onChange={(value) => handleConditionChange('asstTpCd', value)}
          defaultValue={condition?.asstTpCd}
          popupUpPosition={true}
          style={{ width: '100px' }}
        />
        &nbsp;
        <Button
          css={IconButton.button}
          className="create primaryBtn"
          disableRipple
          onClick={() => {
            /*const chCond = condition;
              chCond['asstTpCd'] = AsstTpCd.EQUIPMENT;
              setCondition(chCond);*/
            if (_.isEmpty(condition?.asstTpCd) || condition?.asstTpCd == null) {
              openMessageBar({
                type: 'error',
                content: t(
                  'asst.label.분류 필수 선택입니다.',
                  '항목이 선택되지 않았습니다.<br/>선택 후 진행 할 수 있습니다.'
                ),
              });
              return;
            }

            if (AsstTpCd.SPARE_PARTS == condition?.asstTpCd) {
              openMessageBar({
                type: 'error',
                content: t('asst.label.신규 요청 할 수 없습니다.', '신규 생성 할 수 없습니다.'),
              });
              return;
            }

            generateRvwReqId().then((result) => {
              if (!result) {
                openMessageBar({
                  type: 'error',
                  content: t(
                    'asst.label.검토요청번호 생성에 실패했습니다.<br/>새로고침 후 다시 시도해 주세요.',
                    '검토요청번호 생성에 실패했습니다.<br/>새로고침 후 다시 시도해 주세요.'
                  ),
                });
                return;
              }
              setRequestModalCondition({
                pageId: ApproveRequestPageType.IDLE_ASST_REQ,
                rvwReqId: result,

                rvmAsstTpCd: condition?.asstTpCd,
              });
              setOpenRequestModal(true);
            });
          }}
        >
          {t('com.button.신규', '신규')}
        </Button>
      </GlobalBtnGroup>

      {isOpenRequestModal && (
        <ApproveRequestModal
          open={isOpenRequestModal}
          close={() => setOpenRequestModal(false)}
          pageId={requestModalCondition?.pageId}
          aprReqId={requestModalCondition?.aprReqId}
          condition={{
            rvwReqId: requestModalCondition?.rvwReqId,
            rvmAsstTpCd: requestModalCondition?.rvmAsstTpCd,
          }}
        />
      )}

      {isOpenUserModal && (
        <UserModal
          open={isOpenUserModal}
          close={() => setOpenUserModal(false)}
          title={t('com.label.담당자 조회', '담당자 조회')}
          singleSelect={true}
          defaultUserId={condition?.dataInsUserId}
          onCallback={(result) => {
            const user = result as Employee;
            setCondition({
              ...condition,
              dataInsUserId: user?.userId,
            });
            setDisplayCondition({
              ...displayCondition,
              dataInsUserNm: user?.empNm || '',
            });
          }}
          condition={{ empNm: displayCondition?.dataInsUserNm }}
        />
      )}
    </>
  );
};

export default IdlAssetReviewListPage;
